import { useEffect, useRef, useState } from 'react'
import type { MediaView } from '@olmt/shared'
import { useMediaUrl } from '../hooks/useMediaUrl'
import { downloadMedia } from '../lib/media'
import { formatBytes, formatDuration } from '../lib/time'
import { useT } from '../lib/i18n'
import {
  SEEK_STEP_SECONDS,
  SPEEDS,
  fractionFromPosition,
  nextSpeed,
  secondsFromFraction,
  speedLabel,
  steppedSeconds,
  type VoiceSpeed,
} from '../lib/voice'
import { CloseIcon, DownloadIcon, FileIcon, PauseIcon, PlayIcon, VideoIcon } from './Icons'
import { IconButton } from './IconButton'
import { Spinner } from './Spinner'

type MessageMediaProps = { media: MediaView; own: boolean; fallbackName: string | null }

export function MessageMedia({ media, own, fallbackName }: MessageMediaProps) {
  if (media.kind === 'image') return <ImageMedia media={media} />
  if (media.kind === 'video') return <VideoMedia media={media} />
  if (media.kind === 'voice') return <VoiceMedia media={media} own={own} />
  return <FileMedia media={media} fallbackName={fallbackName} />
}

function ImageMedia({ media }: { media: MediaView }) {
  const t = useT()
  const preview = useMediaUrl(media.thumbUrl ?? media.url)
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block max-w-full overflow-hidden rounded-media bg-surface2 ring-1 ring-line ring-inset"
        aria-label={t('chat.openPhoto')}
      >
        {preview.url ? (
          <img
            src={preview.url}
            alt=""
            className="max-h-80 w-auto max-w-full object-cover"
            width={media.width ?? undefined}
            height={media.height ?? undefined}
          />
        ) : (
          <span className="flex h-40 w-56 items-center justify-center text-muted">
            {preview.error ? t('chat.photoUnavailable') : <Spinner />}
          </span>
        )}
      </button>
      {open && <ImageViewer path={media.url} onClose={() => setOpen(false)} />}
    </>
  )
}

function ImageViewer({ path, onClose }: { path: string; onClose: () => void }) {
  const t = useT()
  const full = useMediaUrl(path)
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('chat.photo')}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 panel-enter"
      onClick={onClose}
    >
      {full.url ? (
        <img src={full.url} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
      ) : (
        <Spinner label={full.error ? t('chat.photoUnavailable') : t('chat.loadingPhoto')} />
      )}
      <button
        ref={closeRef}
        type="button"
        aria-label={t('chat.closePhoto')}
        onClick={onClose}
        className="absolute top-[calc(12px+var(--sat))] right-4 flex size-11 items-center justify-center rounded-full bg-surface/90 text-ink ring-1 ring-line-strong ring-inset"
      >
        <CloseIcon size={22} />
      </button>
    </div>
  )
}

function VideoMedia({ media }: { media: MediaView }) {
  const t = useT()
  const [playing, setPlaying] = useState(false)
  const thumb = useMediaUrl(media.thumbUrl)
  const full = useMediaUrl(playing ? media.url : null)

  if (playing && full.url) {
    return (
      <video
        src={full.url}
        controls
        autoPlay
        playsInline
        className="max-h-80 w-auto max-w-full rounded-media bg-black"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="relative block max-w-full overflow-hidden rounded-media bg-surface2 ring-1 ring-line ring-inset"
      aria-label={playing ? t('chat.loadingVideo') : t('chat.playVideo')}
    >
      {thumb.url ? (
        <img src={thumb.url} alt="" className="max-h-80 w-auto max-w-full object-cover" />
      ) : (
        <span className="flex h-40 w-56 items-center justify-center text-muted">
          <VideoIcon size={32} />
        </span>
      )}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-app/70 text-accent ring-1 ring-accent/60 backdrop-blur-sm">
          {playing && !full.url ? <Spinner /> : <PlayIcon size={26} />}
        </span>
      </span>
      {media.durationMs !== null && (
        <span className="numeric absolute right-2 bottom-2 rounded-md bg-app/85 px-1.5 py-0.5 text-meta text-ink">
          {formatDuration(media.durationMs)}
        </span>
      )}
    </button>
  )
}

/**
 * The player is pinned `dir="ltr"`: elapsed time belongs at the inline end of a transport, and a
 * slider that flips under an Arabic reply would be a worse transport, not a more correct one. The
 * message body around it still follows its own direction.
 *
 * W8 — one row of three controls: the play disc, the speed chip and the track. The chip cycles
 * 1× → 1.5× → 2× and its choice belongs to this note for as long as the message is on screen; it is
 * never written down, so a reload starts at the speed people spoke. The track is a real slider: a
 * drag seeks, the elapsed time follows the finger rather than the audio element, and a drag that
 * began on a playing note gives that playing state back on release. Every rule behind the two lives
 * in `lib/voice.ts`, so the speeds, the clamp and the step size are decided in one place.
 */
function VoiceMedia({ media, own }: { media: MediaView; own: boolean }) {
  const t = useT()
  const audio = useMediaUrl(media.url)
  const elementRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [positionMs, setPositionMs] = useState(0)
  const [speed, setSpeed] = useState<VoiceSpeed>(SPEEDS[0])
  const [scrubbing, setScrubbing] = useState(false)
  /** Whether the note was running when the drag took it — the state the release gives back. */
  const wasPlaying = useRef(false)

  const duration = media.durationMs ?? 0
  const progress = duration > 0 ? Math.min(1, positionMs / duration) : 0

  useEffect(() => {
    const element = elementRef.current
    if (!element) return
    // `defaultPlaybackRate` as well: loading a source resets `playbackRate` to it.
    element.playbackRate = speed
    element.defaultPlaybackRate = speed
  }, [speed, audio.url])

  const seekTo = (fraction: number) => {
    const seconds = secondsFromFraction(fraction, duration)
    // The painted position moves first: while a finger is down, where it is outranks where the
    // audio element believes it is.
    setPositionMs(Math.round(seconds * 1000))
    if (elementRef.current) elementRef.current.currentTime = seconds
  }

  const startScrub = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = elementRef.current
    if (!element || duration <= 0 || event.button !== 0) return
    // A press on the track is the track's gesture: the bubble's long-press (its message menu) must
    // not open halfway through a drag, and the thread's selection still arrives as a click.
    event.stopPropagation()
    wasPlaying.current = !element.paused
    element.pause()
    setScrubbing(true)
    event.currentTarget.setPointerCapture(event.pointerId)
    seekTo(fractionFromPosition(event.clientX, event.currentTarget.getBoundingClientRect()))
  }

  // Captured pointers are what a drag *is*: every move and the release come back to this element,
  // and a pointer that was never captured (a hover, a tap on a note with no duration) is ignored.
  const moveScrub = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    seekTo(fractionFromPosition(event.clientX, event.currentTarget.getBoundingClientRect()))
  }

  const endScrub = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
    event.currentTarget.releasePointerCapture(event.pointerId)
    setScrubbing(false)
    const element = elementRef.current
    if (element && wasPlaying.current) void element.play()
  }

  const onTrackKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const element = elementRef.current
    if (!element || duration <= 0) return
    let seconds: number
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        seconds = steppedSeconds(element.currentTime, -SEEK_STEP_SECONDS, duration)
        break
      case 'ArrowRight':
      case 'ArrowUp':
        seconds = steppedSeconds(element.currentTime, SEEK_STEP_SECONDS, duration)
        break
      case 'Home':
        seconds = secondsFromFraction(0, duration)
        break
      case 'End':
        seconds = secondsFromFraction(1, duration)
        break
      default:
        return
    }
    // The keys are the slider's, not the page's: ArrowDown must not also scroll the thread.
    event.preventDefault()
    element.currentTime = seconds
    setPositionMs(Math.round(seconds * 1000))
  }

  return (
    <div dir="ltr" className="flex min-w-64 items-center gap-2">
      <button
        type="button"
        aria-label={playing ? t('chat.pauseVoiceMessage') : t('chat.playVoiceMessage')}
        onClick={() => {
          const element = elementRef.current
          if (!element) return
          if (element.paused) void element.play()
          else element.pause()
        }}
        className={`press flex size-11 shrink-0 items-center justify-center rounded-full ${
          own ? 'bg-black/25 text-bubble-out-ink' : 'bg-accent text-on-accent'
        }`}
      >
        {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
      </button>
      {/* A chip that is 24px of paint inside a 44px hit box: the pill reads as one more well on the
          bubble, and a thumb gets the target the rest of the app gives it. */}
      <button
        type="button"
        aria-label={t('voice.speedAction', { speed: speedLabel(speed) })}
        onClick={() => setSpeed(nextSpeed(speed))}
        className={`press flex h-11 shrink-0 items-center rounded-full px-1 ${
          own ? 'text-bubble-out-ink' : 'text-accent'
        }`}
      >
        <span className="numeric flex h-6 min-w-9 items-center justify-center rounded-full bg-black/25 px-1.5 text-meta">
          {t('voice.speed', { speed: speedLabel(speed) })}
        </span>
      </button>
      <div
        role="slider"
        tabIndex={duration > 0 ? 0 : -1}
        aria-label={t('voice.seek')}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(positionMs)}
        aria-valuetext={formatDuration(positionMs)}
        aria-disabled={duration <= 0 || undefined}
        onPointerDown={startScrub}
        onPointerMove={moveScrub}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
        onKeyDown={onTrackKeyDown}
        className="flex h-11 min-w-0 flex-1 cursor-pointer touch-none items-center select-none"
      >
        <span className="h-1.5 w-full overflow-hidden rounded-full bg-black/25">
          <span
            className={`block h-full rounded-full ${own ? 'bg-bubble-out-ink/85' : 'bg-accent'}`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </span>
      </div>
      <span className="numeric w-11 shrink-0 text-end text-meta opacity-80">
        {formatDuration(playing || scrubbing || positionMs > 0 ? positionMs : duration)}
      </span>
      {audio.url && (
        <audio
          ref={elementRef}
          src={audio.url}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false)
            setPositionMs(0)
          }}
          onTimeUpdate={(event) => setPositionMs(event.currentTarget.currentTime * 1000)}
        />
      )}
    </div>
  )
}

function FileMedia({ media, fallbackName }: { media: MediaView; fallbackName: string | null }) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const name = fallbackName ?? `file-${media.id.slice(0, 8)}`
  return (
    <div className="flex min-w-56 items-center gap-3">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-panel bg-black/25 text-current">
        <FileIcon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        {/* A file saved from an Arabic phone is named in Arabic; the name is its own paragraph. */}
        <span className="block truncate text-ui font-medium">
          <bdi>{name}</bdi>
        </span>
        <span className="numeric block text-meta opacity-70">{formatBytes(media.bytes)}</span>
      </span>
      <IconButton
        label={t('chat.downloadFile', { name })}
        size={40}
        className="text-current"
        disabled={busy}
        onClick={() => {
          setBusy(true)
          void downloadMedia(media.url, name).finally(() => setBusy(false))
        }}
      >
        {busy ? <Spinner size={18} /> : <DownloadIcon size={18} />}
      </IconButton>
    </div>
  )
}
